// The Day Care board (spec Q4/Q5): one unit, one day — machines down the side,
// shifts across. Each booked session offers exactly its next step: send the
// first session to the admission desk, link the admission the desk made, check
// a repeat session in with one click, or open the running session.

import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import moment from "moment";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import EventNoteIcon from "@mui/icons-material/EventNote";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import RefreshIcon from "@mui/icons-material/Refresh";
import BedIcon from "@mui/icons-material/Hotel";
import { toast } from "react-toastify";

import DaycareScreen from "../components/DaycareScreen";
import ToneChip, { TONES } from "../components/ToneChip";
import {
  cancelSession,
  createBlock,
  deleteBlock,
  fetchDay,
  fetchUnits,
  linkAdmission,
  markDna,
  moveSession,
  releaseSession,
} from "../api/daycareApi";
import { billingFor, checkIn, finishCheckIn, startBilling } from "../api/checkInFlow";
import { boardCounts, groupLabel, nextAction, statusChip } from "../utils/sessionView";
import { fetchPatientAdmissionData } from "../../ipd/pages/patient-admission-ipd/api/registrationService";
import {
  IPD_COLORS,
  IpdButton,
  IpdDateField,
  IpdFormField,
  IpdSectionCard,
  IpdStatCard,
  IpdTextField,
} from "../../ipd/utils";
import { ADMISSION_STATUS, BED_STATUS_VIEW, DAYCARE_BASE, GROUP_CLEAN, SESSION_STATUS } from "../constants";
import { istDayKey } from "../../../utils/istDateTime";

const MACHINE_COL = 190;
const SHIFT_COL = 250;

export function sessionPath(session, date) {
  return `${DAYCARE_BASE}/session/${session.id}?unit=${session.unit_id}&date=${date}`;
}

/** The primary button a card offers for its next step. */
const NEXT_STEP = {
  release: { label: "Send to admission desk", tone: "teal" },
  link: { label: "Link admission", tone: "teal" },
  checkin: { label: "Check in", tone: "teal" },
  finish: { label: "Finish check-in", tone: "amber" },
};

function ReasonDialog({ title, label, confirm, onClose, onConfirm }) {
  const [reason, setReason] = useState("");
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{title}</DialogTitle>
      <DialogContent>
        <IpdFormField label={label} required>
          <IpdTextField autoFocus fullWidth multiline minRows={2} value={reason}
            onChange={(e) => setReason(e.target.value)} />
        </IpdFormField>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <IpdButton variant="outlined" onClick={onClose}>Close</IpdButton>
        <IpdButton disabled={reason.trim().length < 3} onClick={() => onConfirm(reason.trim())}>{confirm}</IpdButton>
      </DialogActions>
    </Dialog>
  );
}

function MoveDialog({ session, unit, onClose, onMoved }) {
  const [machineId, setMachineId] = useState(session.machine_id);
  const [shiftCode, setShiftCode] = useState(session.shift_code);
  const move = async () => {
    const res = await moveSession(session.id, { machine_id: machineId, shift_code: shiftCode });
    if (!res.ok) {
      toast.error(res.message || "Could not move the session");
      return;
    }
    toast.success(res.message || "Session moved");
    onMoved();
  };
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Move session</DialogTitle>
      <DialogContent sx={{ display: "grid", gap: 2 }}>
        <IpdFormField label="Machine">
          <IpdTextField select fullWidth value={machineId} onChange={(e) => setMachineId(e.target.value)}>
            {unit.machines.filter((m) => m.active).map((m) => (
              <MenuItem key={m.id} value={m.id}>{`${m.bed_name} · ${groupLabel(m.viral_group)}`}</MenuItem>
            ))}
          </IpdTextField>
        </IpdFormField>
        <IpdFormField label="Shift">
          <IpdTextField select fullWidth value={shiftCode} onChange={(e) => setShiftCode(e.target.value)}>
            {unit.shifts.map((s) => <MenuItem key={s.code} value={s.code}>{`${s.label} (${s.start}-${s.end})`}</MenuItem>)}
          </IpdTextField>
        </IpdFormField>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <IpdButton variant="outlined" onClick={onClose}>Close</IpdButton>
        <IpdButton onClick={move}>Move</IpdButton>
      </DialogActions>
    </Dialog>
  );
}

function SessionCard({ session, date, onAction, busy }) {
  const history = useHistory();
  const [menu, setMenu] = useState(null);
  const chip = statusChip(session);
  const action = nextAction(session);
  const step = NEXT_STEP[action];
  const booked = session.status === SESSION_STATUS.BOOKED;
  const menuItems = [
    action === "chart" && { kind: "billing", label: "Complete billing" },
    booked && { kind: "move", label: "Move to another machine or shift" },
    booked && { kind: "dna", label: "Mark as did not attend" },
    booked && { kind: "cancel", label: "Cancel session", danger: true },
  ].filter(Boolean);

  const pick = (kind) => {
    setMenu(null);
    onAction(kind, session);
  };

  return (
    <Box sx={{
      height: "100%",
      p: 1.5,
      borderRadius: 1,
      bgcolor: IPD_COLORS.cardBg,
      border: `1px solid ${IPD_COLORS.border}`,
      display: "flex",
      flexDirection: "column",
      gap: 0.75,
    }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography noWrap sx={{ fontSize: 13.5, fontWeight: 700, color: IPD_COLORS.textPrimary }}>
            {session.patient_name || session.patient_id}
          </Typography>
          <Typography noWrap sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>
            {`${session.patient_id}${session.admission_id ? ` · Adm #${session.admission_id}` : ""}`}
          </Typography>
        </Box>
        <ToneChip tone={chip.tone} label={chip.label} />
      </Box>

      <Typography noWrap sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>
        {session.service_name}
        {session.patient_group && session.patient_group !== GROUP_CLEAN && ` · ${groupLabel(session.patient_group)}`}
        {session.retest_due && " · markers retest due"}
      </Typography>

      <Box sx={{ display: "flex", gap: 1, alignItems: "center", mt: "auto", pt: 0.5 }}>
        {step && (
          <IpdButton size="small" tone={step.tone} disabled={busy} onClick={() => onAction(action, session)}>
            {step.label}
          </IpdButton>
        )}
        {session.admission_id && (
          <IpdButton size="small" variant="outlined" onClick={() => history.push(sessionPath(session, date))}>
            Open
          </IpdButton>
        )}
        {menuItems.length > 0 && (
          <>
            <IconButton size="small" aria-label="More actions" onClick={(e) => setMenu(e.currentTarget)} sx={{ ml: "auto" }}>
              <MoreVertIcon fontSize="small" />
            </IconButton>
            <Menu anchorEl={menu} open={Boolean(menu)} onClose={() => setMenu(null)}>
              {menuItems.map((item) => (
                <MenuItem key={item.kind} disabled={busy} onClick={() => pick(item.kind)}
                  sx={{ fontSize: 13, color: item.danger ? IPD_COLORS.error.text : undefined }}>
                  {item.label}
                </MenuItem>
              ))}
            </Menu>
          </>
        )}
      </Box>
    </Box>
  );
}

function EmptySlot({ block, onBlock, onUnblock }) {
  return (
    <Box sx={{
      height: "100%",
      minHeight: 92,
      px: 1.5,
      borderRadius: 1,
      border: `1px dashed ${IPD_COLORS.borderHover}`,
      bgcolor: block ? IPD_COLORS.panelHeaderBg : "transparent",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 1,
    }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: block ? IPD_COLORS.textPrimary : IPD_COLORS.textMuted }}>
          {block ? "Blocked" : "Free"}
        </Typography>
        {block && <Typography noWrap sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>{block.reason}</Typography>}
      </Box>
      <Button size="small" onClick={block ? onUnblock : onBlock}
        sx={{ textTransform: "none", fontSize: 12, fontWeight: 600, color: IPD_COLORS.textSecondary, flexShrink: 0 }}>
        {block ? "Unblock" : "Block"}
      </Button>
    </Box>
  );
}

/** A machine drawn the way the bed-allocation screens draw a bed: a tile tinted
 *  by the bed's state, its code in bold and the state underneath. */
function MachineCell({ machine, bedStatus }) {
  const view = BED_STATUS_VIEW[bedStatus] || { label: bedStatus || "Unknown", tone: "neutral" };
  const colors = TONES[view.tone];
  return (
    <Box sx={{
      height: "100%",
      p: 1.5,
      borderRadius: 1,
      bgcolor: colors.bg,
      border: `1px solid ${colors.border}`,
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
    }}
    >
      <Typography sx={{ fontSize: 15, fontWeight: 800, color: colors.text }}>{machine.bed_name}</Typography>
      <Typography sx={{ fontSize: 12, color: colors.text }}>{view.label}</Typography>
      <Typography sx={{ fontSize: 11.5, color: IPD_COLORS.textSecondary, mt: 0.5 }}>
        {`${groupLabel(machine.viral_group)} patients`}
      </Typography>
    </Box>
  );
}

export default function DaycareBoard() {
  const [units, setUnits] = useState([]);
  const [unitId, setUnitId] = useState("");
  const [date, setDate] = useState(istDayKey(new Date()));
  const [day, setDay] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState(null);

  useEffect(() => {
    fetchUnits()
      .then((list) => {
        setUnits(list || []);
        if (list?.length) setUnitId(list[0].id);
        else setLoading(false);
      })
      .catch((err) => {
        setError(err?.body?.message || "Could not load the day care units");
        setLoading(false);
      });
  }, []);

  const load = useCallback(async () => {
    if (!unitId) return;
    setError("");
    try {
      setDay(await fetchDay(unitId, date));
    } catch (err) {
      setError(err?.body?.message || "Could not load the board");
    } finally {
      setLoading(false);
    }
  }, [unitId, date]);

  useEffect(() => { load(); }, [load]);

  const report = (res, fallback) => {
    if (res.ok) toast.success(res.message || fallback);
    else toast.error(res.message || "Something went wrong");
    return res.ok;
  };

  const runAction = async (kind, session) => {
    if (kind === "cancel" || kind === "move" || kind === "link") {
      setDialog({ kind, session });
      return;
    }
    setBusy(true);
    try {
      if (kind === "release") report(await releaseSession(session.id), "Sent to the admission desk");
      if (kind === "dna") report(await markDna(session.id), "Marked as did not attend");
      if (kind === "checkin") {
        const result = await checkIn(session.id);
        if (result.ok) toast.success(result.message);
        else toast.error(`Check-in stopped at ${result.stage}: ${result.message}`);
      }
      if (kind === "finish") {
        const result = await finishCheckIn(session.admission_id, session.bed_code, billingFor(session, day?.unit));
        if (result.ok) toast.success(result.message);
        else toast.error(`Check-in stopped at ${result.stage}: ${result.message}`);
      }
      if (kind === "billing") {
        const result = await startBilling(session.admission_id, billingFor(session, day?.unit));
        if (result.ok) toast.success(result.message);
        else toast.error(`Billing stopped at ${result.stage}: ${result.message}`);
      }
    } catch (err) {
      toast.error(err?.message || "Something went wrong");
    } finally {
      setBusy(false);
      load();
    }
  };

  const linkFromDesk = async (session) => {
    let admission;
    try {
      admission = await fetchPatientAdmissionData(session.patient_id, { silent: true });
    } catch (err) {
      toast.error(err.message);
      return;
    }
    if (!admission?.admission_id) {
      toast.error("The admission desk has not admitted this patient yet");
      return;
    }
    const linked = await linkAdmission(session.id, admission.admission_id);
    if (!report(linked, "Admission linked")) return;
    setDialog(null);
    // The desk allocated the bed itself, so "Finish check-in" will not be
    // offered: start the billing here instead.
    if (linked.data?.admission_status === ADMISSION_STATUS.ALLOCATED) {
      const result = await startBilling(admission.admission_id, billingFor(session, day?.unit));
      if (!result.ok) toast.error(`Billing stopped at ${result.stage}: ${result.message}`);
    }
    load();
  };

  const shiftDate = (days) => setDate(moment(date).add(days, "day").format("YYYY-MM-DD"));

  const unit = day?.unit;
  const blocks = day?.blocks || [];
  const sessions = day?.sessions || [];
  const counts = boardCounts(day);
  const machines = unit ? unit.machines.filter((m) => m.active) : [];

  return (
    <DaycareScreen
      title="Today's board"
      description="Machines and shifts for the day. Each session shows its next step."
      loading={loading}
      error={error}
      onRetry={load}
      actions={(
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          {units.length > 1 && (
            <IpdTextField select value={unitId} onChange={(e) => setUnitId(e.target.value)} sx={{ minWidth: 180 }}>
              {units.map((u) => <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>)}
            </IpdTextField>
          )}
          <Tooltip title="Previous day">
            <IconButton size="small" onClick={() => shiftDate(-1)} aria-label="Previous day"><ChevronLeftIcon /></IconButton>
          </Tooltip>
          <Box sx={{ width: 170 }}>
            <IpdDateField value={date} onChange={(iso) => iso && setDate(iso)} />
          </Box>
          <Tooltip title="Next day">
            <IconButton size="small" onClick={() => shiftDate(1)} aria-label="Next day"><ChevronRightIcon /></IconButton>
          </Tooltip>
          <IpdButton size="small" variant="outlined" onClick={() => setDate(istDayKey(new Date()))}>Today</IpdButton>
          <IpdButton size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={load}>Refresh</IpdButton>
        </Box>
      )}
    >
      {!unit ? (
        <IpdSectionCard title="No day care unit yet">
          <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 13 }}>
            An administrator sets the unit up under Unit setup: its ward, shifts, services and machines.
          </Typography>
        </IpdSectionCard>
      ) : (
        <>
          <Box sx={{
            display: "grid",
            gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(5, 1fr)" },
            gap: 2,
            mb: 2.5,
          }}
          >
            <IpdStatCard label="Sessions today" value={counts.total} icon={EventNoteIcon} />
            <IpdStatCard label="Waiting to start" value={counts.waiting} icon={HourglassEmptyIcon} tone="amber" />
            <IpdStatCard label="On machine" value={counts.running} icon={MonitorHeartIcon} tone="blue" />
            <IpdStatCard label="Completed" value={counts.done} icon={CheckCircleOutlineIcon} tone="mint" />
            <IpdStatCard label="Machines free" value={`${counts.machinesFree} / ${counts.machines}`} icon={BedIcon} />
          </Box>

          <IpdSectionCard
            title={`${unit.name} · ${moment(date).format("dddd, D MMM YYYY")}`}
            subtitle={`${machines.length} machines · ${unit.shifts.length} shifts${counts.missed ? ` · ${counts.missed} missed` : ""}`}
          >
            <Box sx={{ overflowX: "auto" }}>
              <Box sx={{
                display: "grid",
                gridTemplateColumns: `${MACHINE_COL}px repeat(${unit.shifts.length}, minmax(${SHIFT_COL}px, 1fr))`,
                gap: 1.25,
                minWidth: MACHINE_COL + unit.shifts.length * (SHIFT_COL + 10),
              }}
              >
                <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.8, color: IPD_COLORS.textSecondary,
                  textTransform: "uppercase", alignSelf: "end", pb: 0.5 }}>
                  Machine
                </Typography>
                {unit.shifts.map((s) => (
                  <Box key={s.code} sx={{ pb: 0.5, alignSelf: "end" }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: IPD_COLORS.textPrimary }}>{s.label}</Typography>
                    <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>{`${s.start} – ${s.end}`}</Typography>
                  </Box>
                ))}
                {machines.map((machine) => (
                  <React.Fragment key={machine.id}>
                    <MachineCell machine={machine} bedStatus={day.bed_status[machine.bed_code]} />
                    {unit.shifts.map((shift) => {
                      const session = sessions.find((s) => s.machine_id === machine.id && s.shift_code === shift.code);
                      const block = blocks.find((b) => b.machine_id === machine.id && b.shift_code === shift.code);
                      if (session) {
                        return <SessionCard key={shift.code} session={session} date={date} onAction={runAction} busy={busy} />;
                      }
                      return (
                        <EmptySlot key={shift.code} block={block}
                          onBlock={() => setDialog({ kind: "block", machine, shift })}
                          onUnblock={async () => { if (report(await deleteBlock(block.id), "Block removed")) load(); }} />
                      );
                    })}
                  </React.Fragment>
                ))}
              </Box>
            </Box>
          </IpdSectionCard>
        </>
      )}

      {dialog?.kind === "cancel" && (
        <ReasonDialog title="Cancel session" label="Reason" confirm="Cancel session" onClose={() => setDialog(null)}
          onConfirm={async (reason) => {
            if (report(await cancelSession(dialog.session.id, reason), "Session cancelled")) { setDialog(null); load(); }
          }} />
      )}
      {dialog?.kind === "block" && (
        <ReasonDialog title={`Block ${dialog.machine.bed_name}, ${dialog.shift.label}`}
          label="Reason (e.g. inpatient dialysis, machine service)" confirm="Block" onClose={() => setDialog(null)}
          onConfirm={async (reason) => {
            const res = await createBlock({ unit_id: unit.id, machine_id: dialog.machine.id, block_date: date,
              shift_code: dialog.shift.code, reason });
            if (report(res, "Shift blocked")) { setDialog(null); load(); }
          }} />
      )}
      {dialog?.kind === "move" && (
        <MoveDialog session={dialog.session} unit={unit} onClose={() => setDialog(null)}
          onMoved={() => { setDialog(null); load(); }} />
      )}
      {dialog?.kind === "link" && (
        <Dialog open onClose={() => setDialog(null)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontWeight: 700 }}>Link the desk&apos;s admission</DialogTitle>
          <DialogContent>
            <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary }}>
              {`Once the admission desk has admitted ${dialog.session.patient_name || dialog.session.patient_id} as Day Care on a bed of this unit, link it here.`}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <IpdButton variant="outlined" onClick={() => setDialog(null)}>Close</IpdButton>
            <IpdButton onClick={() => linkFromDesk(dialog.session)}>Link admission</IpdButton>
          </DialogActions>
        </Dialog>
      )}
    </DaycareScreen>
  );
}
